/*
 * Copyright 2026 Jason Figge
 *
 * This file is part of Chip Hippo.
 *
 * Chip Hippo is free software: you can redistribute it and/or modify it under
 * the terms of the GNU General Public License as published by the Free
 * Software Foundation, either version 3 of the License, or (at your option)
 * any later version.
 *
 * Chip Hippo is distributed in the hope that it will be useful, but WITHOUT
 * ANY WARRANTY; without even the implied warranty of MERCHANTABILITY or
 * FITNESS FOR A PARTICULAR PURPOSE. See the GNU General Public License for
 * more details.
 *
 * You should have received a copy of the GNU General Public License along
 * with Chip Hippo. If not, see <https://www.gnu.org/licenses/>.
 */

// w65c22.js — the WDC W65C22 Versatile Interface Adapter (VIA), the CMOS 6522,
// as a PURE, DOM-free state machine behind the STANDARD sequential contract
// ({ state0, step, outputs }). Like sim/w65c21.js (the PIA) and sim/hd44780.js
// (the LCD), it is genuine per-part code the gate/family vocabulary cannot
// express; the def in catalog/chips-io.js is pure data referencing this builder.
//
// The VIA adds to the PIA two 16-bit interval timers (T1/T2), an 8-bit shift
// register, and a unified interrupt-flag / interrupt-enable pair (IFR/IER). The
// 16 registers select from RS3..RS0 (Table 2-1):
//
//   0 ORB/IRB   4 T1C-L   8 T2C-L   C PCR
//   1 ORA/IRA   5 T1C-H   9 T2C-H   D IFR
//   2 DDRB      6 T1L-L   A SR      E IER
//   3 DDRA      7 T1L-H   B ACR     F ORA/IRA (no handshake)
//
// Bus timing follows the PIA/LCD model: the VIA is SELECTED while CS1 is high
// and CS2B low; a READ (RWB high) drives D0–D7 with the addressed register while
// PHI2 is high; a transfer COMMITS on PHI2's FALLING edge. The timers step once
// per PHI2 cycle, on PHI2's RISING edge, through the counts Figures 2-3 and 2-4
// draw: a load holds N for its first cycle ("counts down on the next PHI2
// following the load"), then N-1 … 0, then $FFFF — where the flag sets, N+1.5
// cycles after the write — and T1 then takes its latch again, so a free-running
// T1 times out every N+2 cycles. T2 (one-shot only) runs on from $FFFF.
//
// ── Zero-delay abstraction & scope ───────────────────────────────────────────
// This is a LOGIC-LEVEL, timerless model (the engine has no wall clock — the
// SimController's interval merely produces PHI2 edges). Consequences, all
// faithful to how the rest of the sim works:
//   • Timer periods are measured in PHI2 cycles the user clocks in, not seconds.
//   • The SHIFT REGISTER is fully modeled for the EXTERNAL-clock modes (CB1 in):
//     shift-in (011) takes a bit on each CB1 rise; shift-out (111) puts the next
//     bit on CB2 at each CB1 FALL, so it is steady at the receiver's rise
//     (Figure 2-12), and counts the rise. The internal-clock modes put eight
//     shift pulses on CB1 a byte, as the sheet says — CB1 low one PHI2 cycle,
//     high the next (Figures 2-7 and 2-11): a shift-out mode's bit goes onto
//     CB2 as CB1 falls, a shift-in mode's is taken as it rises. The T2-paced
//     modes (001/100/101) run at that same PHI2 rate rather than N+2 cycles a
//     half pulse — an APPROXIMATION of the pacing only; the eight pulses, the
//     bits and IFR2 are as the sheet draws them.
//   • IRQB is OPEN-DRAIN active-low (the W65C22N model): LOW when an enabled flag
//     is set, else high-impedance (Z) so it wire-ORs onto the processor IRQ.
//
// Ports PA/PB and the control lines CA2/CB1/CB2 are the `io` role — driven when
// programmed as outputs, floating (Z) when inputs. Input latching (ACR bits 0/1)
// captures the port pins at the active CA1/CB1 edge.

import { H, L, X, Z } from "./levels.js";

/** One clean bit from an asInput'd level (H → 1; L/X → 0 — a float reads H). */
const bit = (lv) => (lv === H ? 1 : 0);
const high = (lv) => lv === H;
const b = (v, n) => (v >> n) & 1;

/** A bus line read against the level it must stand at: H when it does, L
    when it is at the other, X when unknown. */
const asked = (lv, want) => (lv === want ? H : lv === H || lv === L ? L : X);
/** All of them: L if any is L, else X if any is unknown, else H. */
const every = (...ts) => (ts.includes(L) ? L : ts.includes(X) ? X : H);
const rose = (p, c) => p === L && c === H;
const fell = (p, c) => p === H && c === L;
const activeEdge = (edgeSelHigh, prev, cur) =>
  edgeSelHigh ? rose(prev, cur) : fell(prev, cur);

// IFR / IER bit positions (Table 2-11).
const F_CA2 = 0;
const F_CA1 = 1;
const F_SR = 2;
const F_CB2 = 3;
const F_CB1 = 4;
const F_T2 = 5;
const F_T1 = 6;

/**
 * The W65C22 VIA behavior as a sequential unit.
 * @param {object} pins - catalog pin numbers.
 * @param {number[]} pins.pa @param {number[]} pins.pb @param {number[]} pins.d
 * @param {number} pins.ca1 @param {number} pins.ca2
 * @param {number} pins.cb1 @param {number} pins.cb2
 * @param {number[]} pins.rs - [RS0, RS1, RS2, RS3]
 * @param {number} pins.cs1 @param {number} pins.cs2b
 * @param {number} pins.rwb @param {number} pins.phi2 @param {number} pins.resb
 * @param {number} pins.irqb
 * @returns {{ state0: Function, step: Function, outputs: Function }}
 */
export function w65c22Unit(pins) {
  const {
    pa,
    pb,
    d,
    ca1,
    ca2,
    cb1,
    cb2,
    rs,
    cs1,
    cs2b,
    rwb,
    phi2,
    resb,
    irqb,
  } = pins;

  const selected = (ins) => high(ins.get(cs1)) && !high(ins.get(cs2b));
  const regSelect = (ins) =>
    rs.reduce((n, pin, i) => n | (bit(ins.get(pin)) << i), 0);
  const readData = (ins) =>
    d.reduce((n, pin, i) => n | (bit(ins.get(pin)) << i), 0);
  const pinsToByte = (list, ins) =>
    list.reduce((n, pin, i) => n | (bit(ins.get(pin)) << i), 0);

  // Shift-register mode decode from ACR bits 4..2 (Table 2-10).
  const srMode = (s) => (s.acr >> 2) & 7;
  const srEnabled = (s) => srMode(s) !== 0;
  const srShiftOut = (s) => (srMode(s) & 4) !== 0;
  const srExternal = (s) => srMode(s) === 3 || srMode(s) === 7;
  const srFreeRun = (s) => srMode(s) === 4;

  /** Port A read value: the latched IRA (ACR0) or the live pin levels. */
  const readPortA = (s, ins) => (b(s.acr, 0) ? s.ira : pinsToByte(pa, ins));

  /** Port B read value: ORB for output lines, else the latch or the pin. */
  const readPortB = (s, ins) =>
    pb.reduce((n, pin, i) => {
      const val = b(s.ddrb, i)
        ? b(s.orb, i)
        : b(s.acr, 1)
          ? b(s.irb, i)
          : bit(ins.get(pin));
      return n | (val << i);
    }, 0);

  /** IRQ asserted when any enabled flag is set. */
  const irqActive = (s) => (s.ifr & s.ier & 0x7f) !== 0;

  /** The value a register presents on a read — PURE (no side effects). */
  const readRegister = (s, ins) => {
    switch (regSelect(ins)) {
      case 0x0:
        return readPortB(s, ins);
      case 0x1:
      case 0xf:
        return readPortA(s, ins);
      case 0x2:
        return s.ddrb;
      case 0x3:
        return s.ddra;
      case 0x4:
        return s.t1c & 0xff;
      case 0x5:
        return (s.t1c >> 8) & 0xff;
      case 0x6:
        return s.t1l & 0xff;
      case 0x7:
        return (s.t1l >> 8) & 0xff;
      case 0x8:
        return s.t2c & 0xff;
      case 0x9:
        return (s.t2c >> 8) & 0xff;
      case 0xa:
        return s.sr;
      case 0xb:
        return s.acr;
      case 0xc:
        return s.pcr;
      case 0xd:
        return s.ifr | (irqActive(s) ? 0x80 : 0);
      default:
        return s.ier | 0x80; // 0xe
    }
  };

  /** CA2 output level, or null (input → floats). PCR bits 3..1. */
  const ca2Out = (s) => {
    const m = (s.pcr >> 1) & 7;
    if (m < 4) return null;
    if (m === 6) return L;
    if (m === 7) return H;
    return s.ca2; // 100 handshake / 101 pulse → tracked level
  };
  /** CB2 output level, or null. Serial-out (SR) wins; else PCR bits 7..5. */
  const cb2Out = (s) => {
    if (srEnabled(s)) return srShiftOut(s) ? s.srDataOut : null; // serial data
    const m = (s.pcr >> 5) & 7;
    if (m < 4) return null;
    if (m === 6) return L;
    if (m === 7) return H;
    return s.cb2;
  };

  // ── Timer / shift helpers (mutate `next`, read `state`) ──────────────────────
  // T1's counts (Figures 2-3, 2-4): N held the cycle after a load, N-1 … 0,
  // then $FFFF — where it times out — and then the latch again, in either mode
  // (one-shot only flags once). So the first timeout is N+1.5 cycles after the
  // T1C-H write and a free-running one repeats every N+2, not N+1.
  const decrementT1 = (next, state) => {
    const freerun = b(state.acr, 6) === 1;
    const pb7en = b(state.acr, 7) === 1;
    if (state.t1Hold) {
      next.t1Hold = false; // the cycle after a load counts nothing
    } else if (state.t1Reload) {
      next.t1Reload = false;
      next.t1c = state.t1l; // the cycle after $FFFF takes the latch
    } else if (state.t1c === 0) {
      if (state.t1Armed) {
        next.ifr |= 1 << F_T1;
        if (pb7en) next.pb7 = freerun ? (state.pb7 === H ? L : H) : H;
        if (!freerun) next.t1Armed = false; // one-shot fires once
      }
      next.t1c = 0xffff;
      next.t1Reload = true;
    } else {
      next.t1c = (state.t1c - 1) & 0xffff;
    }
  };

  const decrementT2 = (next, state) => {
    if (next.t2c === 0) {
      if (state.t2Armed) {
        next.ifr |= 1 << F_T2;
        next.t2Armed = false; // T2 is always one-shot
      }
      next.t2c = 0xffff;
    } else {
      next.t2c = (next.t2c - 1) & 0xffff;
    }
  };

  /** Shift one bit; `sample` is the CB2 input bit for shift-in modes. */
  const shiftOnce = (next, state, sample) => {
    if (srShiftOut(state)) {
      const outBit = (state.sr >> 7) & 1;
      next.sr = ((state.sr << 1) | outBit) & 0xff; // rotate (recirculate)
      next.srDataOut = outBit ? H : L;
    } else {
      next.sr = ((state.sr << 1) | sample) & 0xff; // new bit enters bit 0
    }
  };

  return {
    state0() {
      // Reset clears the registers and floats the ports; the timers hold their
      // (undefined) counts disarmed; the shift clock / serial out idle HIGH.
      return {
        ora: 0,
        orb: 0,
        ddra: 0,
        ddrb: 0,
        ira: 0,
        irb: 0,
        t1c: 0,
        t1l: 0,
        t2c: 0,
        t2ll: 0,
        t1Armed: false,
        t2Armed: false,
        t1Hold: false,
        t1Reload: false,
        t2Hold: false,
        sr: 0,
        srCount: 0,
        srActive: false,
        srClk: H,
        srDataOut: H,
        acr: 0,
        pcr: 0,
        ifr: 0,
        ier: 0,
        pb7: H,
        ca2: H,
        cb2: H,
        ca2pulse: false,
        cb2pulse: false,
      };
    },

    step(state, ins, prev) {
      if (ins.get(resb) === L) return this.state0(); // async reset

      const next = { ...state };
      const p = prev ?? new Map();
      const was = (pin) => (prev ? p.get(pin) : undefined);

      const caMode = (state.pcr >> 1) & 7; // CA2 control
      const cbMode = (state.pcr >> 5) & 7; // CB2 control
      const usingCb1Clock = srEnabled(state) && srExternal(state);

      // ── CA1 interrupt / latch / handshake return ───────────────────────────
      if (prev && activeEdge(b(state.pcr, 0), was(ca1), ins.get(ca1))) {
        next.ifr |= 1 << F_CA1;
        if (b(state.acr, 0)) next.ira = pinsToByte(pa, ins); // latch IRA
        if (caMode === 4) next.ca2 = H; // handshake output returns high
      }
      // ── CB1: either the SR external shift clock, or the CB1 interrupt ───────
      if (usingCb1Clock) {
        // Shift-out (111) changes CB2 on CB1's FALL, so the bit is steady at
        // the rise a receiver latches on (Figure 2-12); shift-in (011) takes
        // CB2 at the rise. Either way the rise is the pulse counted.
        const out = srShiftOut(state);
        if (out && prev && fell(was(cb1), ins.get(cb1))) {
          shiftOnce(next, state, 0);
        }
        if (prev && rose(was(cb1), ins.get(cb1))) {
          if (!out) shiftOnce(next, state, bit(ins.get(cb2)));
          next.srCount = state.srCount + 1;
          if (next.srCount >= 8) {
            next.ifr |= 1 << F_SR;
            next.srCount = 0; // recount the next eight (pulse counter)
          }
        }
      } else if (prev && activeEdge(b(state.pcr, 4), was(cb1), ins.get(cb1))) {
        next.ifr |= 1 << F_CB1;
        if (b(state.acr, 1)) next.irb = pinsToByte(pb, ins); // latch IRB
        if (cbMode === 4) next.cb2 = H;
      }
      // ── CA2 / CB2 as interrupt inputs (modes 0..3) ─────────────────────────
      if (
        caMode < 4 &&
        prev &&
        activeEdge(caMode & 2 ? 1 : 0, was(ca2), ins.get(ca2))
      ) {
        next.ifr |= 1 << F_CA2;
      }
      if (
        !srEnabled(state) &&
        cbMode < 4 &&
        prev &&
        activeEdge(cbMode & 2 ? 1 : 0, was(cb2), ins.get(cb2))
      ) {
        next.ifr |= 1 << F_CB2;
      }

      // ── PHI2 rising edge: advance the timers + internal shift clock ─────────
      if (prev && rose(was(phi2), ins.get(phi2))) {
        decrementT1(next, state);
        // T2 holds its load's first cycle too (Figure 2-3); in pulse-counting
        // mode the hold simply lapses.
        next.t2Hold = false;
        if (b(state.acr, 5) === 0 && !state.t2Hold) decrementT2(next, state);
        // Internal (PHI2/T2-paced) shifting — one CB1 pulse a bit, low for a
        // PHI2 cycle and high for the next: eight pulses a byte.
        if (srEnabled(state) && !srExternal(state)) {
          const run = srFreeRun(state) || (state.srActive && state.srCount < 8);
          if (run && state.srClk === H) {
            next.srClk = L; // CB1 falls: a shift-out mode's next bit goes out
            if (srShiftOut(state)) shiftOnce(next, state, 0);
          } else if (run) {
            next.srClk = H; // CB1 rises: the bit is taken
            if (!srShiftOut(state)) shiftOnce(next, state, bit(ins.get(cb2)));
            if (!srFreeRun(state)) {
              next.srCount = state.srCount + 1;
              if (next.srCount >= 8) {
                next.ifr |= 1 << F_SR;
                next.srActive = false;
              }
            }
          }
        }
      }
      // ── T2 pulse-counting mode: decrement on PB6 falling edges ──────────────
      // (Its flag is set as the count REACHES zero — the W65C22S sheet §2.10,
      // Fig. 2-5 — not at the roll to $FFFF the timed mode's is: N pulses,
      // not N + 1. The grade, 2026-10-23.)
      if (b(state.acr, 5) === 1 && prev && fell(was(pb[6]), ins.get(pb[6]))) {
        if (next.t2c === 1 && state.t2Armed) {
          next.ifr |= 1 << F_T2;
          next.t2Armed = false;
          next.t2c = 0;
        } else {
          decrementT2(next, state);
        }
      }

      // A one-cycle CA2/CB2 pulse from the PREVIOUS cycle ends on this PHI2
      // fall whether or not the chip is selected — the next cycle is seldom
      // another access to it.
      const phi2Fell = prev && fell(was(phi2), ins.get(phi2));
      if (phi2Fell && state.ca2pulse) {
        next.ca2 = H;
        next.ca2pulse = false;
      }
      if (phi2Fell && state.cb2pulse) {
        next.cb2 = H;
        next.cb2pulse = false;
      }

      // ── Bus transaction: commit on PHI2's falling edge while selected ───────
      if (phi2Fell && selected(ins)) {
        const reg = regSelect(ins);
        const write = ins.get(rwb) === L;
        const data = readData(ins);
        // CA2/CB2 flags are NOT auto-cleared in the "independent" input modes
        // (PCR 001/011) — only by writing the IFR.
        const caIndependent = caMode === 1 || caMode === 3;
        const cbIndependent = cbMode === 1 || cbMode === 3;

        if (write) {
          switch (reg) {
            case 0x0: // ORB
              next.orb = data;
              next.ifr &= ~(1 << F_CB1);
              if (!cbIndependent) next.ifr &= ~(1 << F_CB2);
              if (cbMode === 4 || cbMode === 5) {
                next.cb2 = L; // data-ready handshake / pulse
                if (cbMode === 5) next.cb2pulse = true;
              }
              break;
            case 0x1: // ORA (handshake)
              next.ora = data;
              next.ifr &= ~(1 << F_CA1);
              if (!caIndependent) next.ifr &= ~(1 << F_CA2);
              if (caMode === 4 || caMode === 5) {
                next.ca2 = L;
                if (caMode === 5) next.ca2pulse = true;
              }
              break;
            case 0x2:
              next.ddrb = data;
              break;
            case 0x3:
              next.ddra = data;
              break;
            case 0x4: // T1C-L (low latch)
            case 0x6: // T1L-L
              next.t1l = (state.t1l & 0xff00) | data;
              break;
            case 0x5: // T1C-H: load high latch, transfer latch→counter, start
              next.t1l = (state.t1l & 0x00ff) | (data << 8);
              next.t1c = next.t1l;
              next.t1Hold = true;
              next.t1Reload = false;
              next.ifr &= ~(1 << F_T1);
              next.t1Armed = true;
              // PB7 goes low on the load in EITHER mode (Figures 2-3, 2-4).
              if (b(state.acr, 7)) next.pb7 = L;
              break;
            case 0x7: // T1L-H: load high latch only (no transfer)
              next.t1l = (state.t1l & 0x00ff) | (data << 8);
              next.ifr &= ~(1 << F_T1);
              break;
            case 0x8: // T2C-L (low latch)
              next.t2ll = data;
              break;
            case 0x9: // T2C-H: load high, transfer low latch, start
              next.t2c = (data << 8) | state.t2ll;
              next.t2Hold = true;
              next.ifr &= ~(1 << F_T2);
              next.t2Armed = true;
              break;
            case 0xa: // SR
              next.sr = data;
              next.ifr &= ~(1 << F_SR);
              next.srCount = 0;
              next.srActive = true;
              break;
            case 0xb:
              next.acr = data;
              break;
            case 0xc:
              next.pcr = data;
              break;
            case 0xd: // IFR: writing 1 clears a flag
              next.ifr &= ~(data & 0x7f);
              break;
            case 0xe: // IER: bit7 = set/clear the marked enables
              next.ier =
                data & 0x80
                  ? state.ier | (data & 0x7f)
                  : state.ier & ~(data & 0x7f);
              break;
            case 0xf: // ORA (no handshake): no flag clear, no CA2 handshake
              next.ora = data;
              break;
          }
        } else {
          switch (reg) {
            case 0x0: // IRB
              next.ifr &= ~(1 << F_CB1);
              if (!cbIndependent) next.ifr &= ~(1 << F_CB2);
              break;
            case 0x1: // IRA (handshake)
              next.ifr &= ~(1 << F_CA1);
              if (!caIndependent) next.ifr &= ~(1 << F_CA2);
              if (caMode === 4 || caMode === 5) {
                next.ca2 = L; // data-taken handshake / pulse
                if (caMode === 5) next.ca2pulse = true;
              }
              break;
            case 0x4: // read T1C-L clears the T1 flag
              next.ifr &= ~(1 << F_T1);
              break;
            case 0x8: // read T2C-L clears the T2 flag
              next.ifr &= ~(1 << F_T2);
              break;
            case 0xa: // read SR clears its flag, restarts the shift-in count
              next.ifr &= ~(1 << F_SR);
              next.srCount = 0;
              next.srActive = true;
              break;
            // 0xf (IRA no-handshake) and the rest have no read side effects.
          }
        }
      }

      return next;
    },

    outputs(state, ins) {
      const out = new Map();

      // Peripheral A: output lines drive ORA; input lines float.
      pa.forEach((pin, i) =>
        out.set(pin, b(state.ddra, i) ? (b(state.ora, i) ? H : L) : Z),
      );
      // Peripheral B, with two overrides: PB7 as the T1 output (ACR7), and PB6
      // as the T2 pulse-count input (ACR5 → always an input, floats).
      pb.forEach((pin, i) => {
        if (i === 7 && b(state.acr, 7)) out.set(pin, state.pb7);
        else if (i === 6 && b(state.acr, 5)) out.set(pin, Z);
        else out.set(pin, b(state.ddrb, i) ? (b(state.orb, i) ? H : L) : Z);
      });

      // Control lines.
      out.set(ca2, ca2Out(state) ?? Z);
      out.set(cb2, cb2Out(state) ?? Z);
      // CB1 drives the internal shift clock; otherwise it's an input.
      out.set(cb1, srEnabled(state) && !srExternal(state) ? state.srClk : Z);

      // Data bus during a selected read while PHI2 is high.
      // A select, R/W̄ or PHI2 unknown that could make it one drives X
      // (the regrade, 2026-10-26: read as deselected, the CPU took $FF).
      const driving = every(
        asked(ins.get(cs1), H),
        asked(ins.get(cs2b), L),
        asked(ins.get(rwb), H),
        asked(ins.get(phi2), H),
      );
      if (driving === H) {
        const value = readRegister(state, ins);
        d.forEach((pin, i) => out.set(pin, (value >> i) & 1 ? H : L));
      } else {
        for (const pin of d) out.set(pin, driving === X ? X : Z);
      }

      // IRQB: open-drain, LOW when asserted.
      out.set(irqb, irqActive(state) ? L : Z);
      return out;
    },
  };
}
